/**
 * Action builders for room CRUD flows.
 */
import {
  createPersistentRoom,
  deletePersistentRoom,
  getPersistentRoom,
  getUserPersistentRooms,
  updatePersistentRoom,
} from '../services/firebase/persistentRooms';
import type { CreateRoomInput, UpdateRoomInput } from '../types/room';
import type { RoomStoreSet } from './roomStoreTypes';

/**
 * Builds the load user rooms action.
 */
export function loadUserRoomsAction(set: RoomStoreSet) {
  return async (userId: string, email?: string): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      const rooms = await getUserPersistentRooms(userId, email);
      set({ rooms, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load rooms';
      set({ error: message, isLoading: false });
    }
  };
}

/**
 * Builds the create room action.
 */
export function createRoomAction(set: RoomStoreSet) {
  return async (
    input: CreateRoomInput,
    user: { uid: string; email: string; displayName: string }
  ) => {
    set({ isLoading: true, error: null });

    try {
      const room = await createPersistentRoom(input, user);
      set((state) => ({
        rooms: [...state.rooms, room],
        isLoading: false,
      }));
      return room;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create room';
      set({ error: message, isLoading: false });
      throw error;
    }
  };
}

/**
 * Builds the load room action.
 */
export function loadRoomAction(set: RoomStoreSet) {
  return async (roomId: string): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      const room = await getPersistentRoom(roomId);
      if (!room) {
        set({ error: 'Room not found', isLoading: false, currentRoom: null });
        return;
      }
      set({ currentRoom: room, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load room';
      set({ error: message, isLoading: false });
    }
  };
}

/**
 * Builds the update room action.
 */
export function updateRoomAction(set: RoomStoreSet) {
  return async (roomId: string, input: UpdateRoomInput): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await updatePersistentRoom(roomId, input);
      const updatedRoom = await getPersistentRoom(roomId);

      set((state) => ({
        rooms: state.rooms.map((room) =>
          room.id === roomId && updatedRoom ? updatedRoom : room
        ),
        currentRoom:
          state.currentRoom?.id === roomId ? updatedRoom : state.currentRoom,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update room';
      set({ error: message, isLoading: false });
      throw error;
    }
  };
}

/**
 * Builds the delete room action.
 */
export function deleteRoomAction(set: RoomStoreSet) {
  return async (roomId: string): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await deletePersistentRoom(roomId);
      set((state) => ({
        rooms: state.rooms.filter((room) => room.id !== roomId),
        currentRoom:
          state.currentRoom?.id === roomId ? null : state.currentRoom,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete room';
      set({ error: message, isLoading: false });
      throw error;
    }
  };
}
